import {
  generalTerm,
} from './en.shared.ts';

import {
  TComponentPT9800Keys, TComponentPT9800Value, TComputedPT9800PlannedPartInput,
  Tpt9800TrlTokens,
} from '@/types/openapi-type.ts';

const processFlowManualCommand: TComponentPT9800Keys<'processFlowManualCommand'> = {
  acknowledged: 'Acknowledged',
  additionalInfoColumnCaption: 'Additional Info Caption',
  additionalInfoColumnValue: 'Additional Info Value',
  idFieldName: 'ID Field Name',
  idFieldValue: 'ID Field Value',
  ldArchivCrit1Name: 'Archiv-Crit1 Caption',
  ldArchivCrit1Value: 'Archiv-Crit1 Value',
  orderNumberCaption: 'Order Number Caption',
  orderNumberValue: 'Order Number Value',
  priority: 'Priority',
  rowId: 'Row ID',
  source: 'Source',
  sourceWpId: 'Source WP ID',
  target: 'Target',
  targetWpId: 'Target WP ID',
  wpId: 'Car WP ID',
};

const recipeRuntimeInfo: TComponentPT9800Keys<'recipeRuntimeInfo'> = {
  actualStepDuration: 'Actual Step Duration',
  loadIdentifier: generalTerm.loadIdentifier,
};

const recipeListItem: TComponentPT9800Keys<'recipeListItem'> = {
  id: 'id',
  description: generalTerm.description,
  recipe: generalTerm.recipe,
  recipePrefix: 'Recipe prefix',
  version: 'Recipe version',
};

const recipeItemHeader: Partial<TComponentPT9800Keys<'batchRecipeItemHeader'>> = {
  recipeName: 'Name',
  version: generalTerm.revision,
  recipePrefix: 'Group',
  description: 'Description',
  processType: 'Process',
  processId: 'Operation',
  comment: 'Comment',
  diff: 'Activate',
  correction: 'Correction',
};

const material: Partial<TComponentPT9800Keys<'material'>> = {
  // Diffusion field labels reuse the shared generalTerm wording so they read identically here, in
  // the Diffusion Inputs panel and in the simulator.
  carbonContent: generalTerm.carbonContent,
  carbideLimit: generalTerm.carbideLimit,
  alloyFactor: generalTerm.alloyFactor,
  customerMaterialId: 'Material',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchingIntensity: generalTerm.quenchingIntensity,
  representativeDiameter: generalTerm.representativeDiameter,
  requiredHardness: generalTerm.requiredHardness,
  targetSurfC: generalTerm.targetSurfaceCarbon,
  co: generalTerm.carbonMonoxide,
  h2: generalTerm.hydrogen,
};

const beltRecipeItemHeader: Partial<TComponentPT9800Keys<'beltRecipeItemHeader'>> = {
  description1: 'Description 1',
  description2: 'Description 2',
  description3: 'Description 3',
  description4: 'Description 4',
  processId: 'Operation',
  processDepth: 'Carburizing depth',
  processDepthAt: 'Carbon content at carburizing depth',
};

const pusherRecipeItemHeader: Partial<TComponentPT9800Keys<'pusherRecipeItemHeader'>> = {
  description: beltRecipeItemHeader.description1,
  description1: beltRecipeItemHeader.description2,
  processDepth: beltRecipeItemHeader.processDepth,
  processDepthAt: beltRecipeItemHeader.processDepthAt,
  caseHardeningDepthFrom: 'Case hardening depth from',
  caseHardeningDepthTo: 'Case hardening depth to',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchIntensity: generalTerm.quenchingIntensity,
  partId: generalTerm.part,
};

const recipeItemPosition: Partial<TComponentPT9800Keys<'recipeItemPosition'>> = {
  setpoints: 'Setpoints',
  endAfter: 'End condition',
  digitalOutputList: 'Digital outputs',
  digitalInputList: 'Digital inputs',
};

const recipeItemPositionHeader: Partial<TComponentPT9800Keys<'recipeItemPositionHeader'>> = {
  positionNo: 'Step',
  description: 'Step name',
  chamberId: 'Chamber',
  chamberOperationId: 'Chamber operation',
  afterId: 'End condition',
  estimatedDurationInMin: 'Estimated duration',
  endAtPerc: 'End at %cd',
  afterTimeInMin: 'End condition duration',
};

const plannedPartInput: Partial<Record<keyof TComputedPT9800PlannedPartInput, string>> = {
  pos: 'Position',
  partName: 'Part number',
  partDescription: 'Part description',
  orderNo: generalTerm.loadIdentifier,
  orderRemark: 'Order remark',
  customerOrderNumber: 'Customer order No',
  amount: 'Amount',
  weight: 'Weight',
  totalWeight: 'Total weight',
};

const processFlowContent: Partial<TComponentPT9800Keys<'processFlowContent'>> = {
  name: 'Content No',
  weight: 'PLC Weight',
  createdTimestamp: 'Created At',
  modifiedTimestamp: 'Modified At',
};

const processFlow: Partial<TComponentPT9800Keys<'processFlow'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
  updateTimestamp: 'Last update',
  automatNo: 'Automat No',
  comment1: 'Comment1',
  comment2: 'Comment2',
  comment3: 'Comment3',
};

const processFlowListItem: Partial<TComponentPT9800Keys<'processFlowListItem'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
};

const planLoadInput: Partial<TComponentPT9800Keys<'planLoadInput'>> = {
  workplaceWoNumber: generalTerm.loadIdentifier,
};

const setPointItem: Partial<TComponentPT9800Keys<'setPointItem'>> & {
  unit: string,
} = {
  userComponentId: 'Setpoint',
  controlId: 'Ctrl',
  value: 'Value',
  unit: 'Unit',
  toleranceMax: 'Tol +',
  toleranceMin: 'Tol -',
  alarmStartTimeInMin: 'Alarm start',
  alarmToleranceTimeInMin: 'Alarm tolerance',
  endControlId: 'End ctrl',
  endValue: 'End condition',
};

const furnaceAlarm: Partial<TComponentPT9800Keys<'furnaceAlarm'>> = {
  wpName: 'Workplace',
  ackTime: 'Ack',
  alarmBit: 'Bit',
  alarmByte: 'Byte',
  endTime: 'End',
  gate: 'Gate',
  alarmNo: 'Id',
  message: 'Message',
  priority: 'Priority',
  startTime: 'Start',
};

const load: Partial<TComponentPT9800Keys<'load'>> = {
  name: generalTerm.jobDisplayName,
  startTime: generalTerm.jobStartedAtUtc,
  endTime: generalTerm.jobFinishedAtUtc,
  identifier: generalTerm.loadIdentifier,
  recipe: generalTerm.recipe,
  partsText: plannedPartInput.partDescription,
  workOrderNo: plannedPartInput.orderNo,
  orderRemark: plannedPartInput.orderRemark,
  status: 'State',
  comment: 'Comment',
  processFlowLoadName: 'Process Flow Load',
  packedTime: 'Packed Time',
  packedUser: 'Packed By',
  startedUser: 'Started By',
  earliestTimeStart: 'Earliest Start',
};

const loadState: Record<TComponentPT9800Value<'load'>['status'], string> = {
  EXT_START: 'Started (external)',
  FINISHED: 'Finished',
  PLANNED: 'Planned',
  RUNNING: 'Running',
  RUNNING_LOADING_ENDED: 'Running (loading ended)',
  STARTED: 'Started',
  STOPPED: 'Stopped',
  TOO_OLD_NEVER_STARTED: 'Never started',
  UNKNOWN: 'Unknown',
  WAITING_FOR_CONDITIONS: 'Waiting for conditions',
  WAITING_FOR_LOADING: 'Waiting for loading',
};

const WorkPlaceStatus: Partial<TComponentPT9800Keys<'WorkPlaceStatus'>> = {
  connected: 'connected',
  recipe: 'actualStep',
  remote: 'remote',
  hold: 'hold',
};

const WorkPlaceStatusStatus: Record<Exclude<TComponentPT9800Value<'WorkPlaceStatus'>['status'], undefined>, string> = {
  ALARM: 'Alarm',
  IDLE: 'Idle',
  NO_REMOTE_CONNECTION: 'No remote connection',
  RUNNING: 'Running recipe',
  SWITCHED_OFF: 'Switched off',
};

export const pt9800Translations = {
  T_MENU_GROUP_USER: 'User',
  T_MENU_GROUP_TERMINAL: 'Terminal',
  T_MENU_GROUP_SYSTEM_1: 'System-1',
  T_MENU_GROUP_SYSTEM_2: 'System-2',
  T_MENU_ITEM_USER_GROUPS: 'User groups',
  T_MENU_ITEM_USERS: 'Users',
  T_MENU_ITEM_APPLICATION_GROUPS: 'Application groups',
  T_MENU_ITEM_APPLICATIONS: 'Applications',
  T_MENU_ITEM_USER_ACCESS: 'User access',
  T_MENU_ITEM_MATERIAL_MANAGEMENT: 'Material',
  T_MENU_ITEM_PART_MANAGEMENT: 'Part management',
  T_MENU_ITEM_PART_CUSTOMERS: 'Customers',
  T_MENU_ITEM_TERMINAL_TYPE: 'Terminal type',
  T_MENU_ITEM_TERMINAL: 'Terminal',
  T_MENU_ITEM_CLIENT_ACCESS: 'Client access',
  T_MENU_ITEM_COLOR_ADMINISTRATION: 'Color',
  T_MENU_ITEM_BOX_ADMINISTRATION: 'Box',
  T_MENU_ITEM_ALARMS: 'Alarms',
  T_MENU_ITEM_BACKGROUND_PROCESSES: 'Background processes',
  T_MENU_ITEM_TASKS: 'Tasks',
  T_MENU_ITEM_WORKPLACES: 'Workplaces',
  T_MENU_ITEM_OPERATIONS: 'Operations',
  T_MENU_GROUP_SYSTEM_DATA: 'System data',
  T_MENU_GROUP_EVALUATION: 'Evaluation',
  T_MENU_GROUP_PART: 'Part',
  T_MENU_GROUP_SPECIAL: 'Special',
  T_MENU_ITEM_PRODUCTION_LOGS: 'Production logs',
  T_MENU_ITEM_WORKPLACE_TYPE: 'Workplace type',

  T_SHOP_FURNACE_STATUS_NOT_CONNECTED: 'Blue=Equipment Not Connected',
  T_SHOP_FURNACE_STATUS_MANUAL_MODE: 'White=Manual Mode',
  T_SHOP_FURNACE_STATUS_NO_TREATMENT_RUNNING: 'Yellow=No Treatment Running',
  T_SHOP_FURNACE_STATUS_PLC_DENIES_START: 'Pink=PLC denies Recipe-Start',
  T_SHOP_FURNACE_STATUS_RUNNING: 'Green=Treatment Running - No Alarms',
  T_SHOP_FURNACE_STATUS_ALARM: 'Red=Alarms Exist',
  T_SHOP_FURNACE_STATUS_OFF: 'LightGray=Switched Off (see Basedata Workplace)',
  T_SHOP_FURNACE_STATUS_MAINTENANCE: 'DarkGray=In Maintenance (see Basedata Workplace)',
  T_SHOP_FURNACE_STATUS_CONFIG_ERROR: 'Cyan=Something is WRONG with this Configuration',
  T_SHOP_FURNACE_STATUS_ENERGY_MAN_DISABLED_START: 'Pink=EnergyManagement disables Recipe-Start',
  T_SHOP_FURNACE_STATUS_WEEKEND_MODE: 'Weekend-Mode',
  T_SHOP_FURNACE_STATUS_NO_ALARMS: 'Green=No Alarms',

  // SHOPLAYOUT - CAR
  T_SHOP_STATUS_CELL_READY_FOR_WORK: 'Cell is ready for work',
  T_SHOP_STATUS_CELL_NOT_READY_FOR_WORK: 'Cell is NOT ready for work !!! ',

  T_SHOP_CAR_STATUS_NO_PLC_SIMULATE: '!!! OHNE_SPS==>Simulate !!! ',
  T_SHOP_CAR_STATUS_MANUAL: 'MANUAL',
  T_SHOP_CAR_STATUS_AUTO: 'Auto',
  T_SHOP_CAR_STATUS_SCHED: 'SCHED',
  T_SHOP_CAR_STATUS_NOT_ACTIVE: 'CHARGE CAR NOT ACTIVE',
  T_SHOP_CAR_STATUS_LOGIC_NOT_RUNNING: 'Logic not running',
  T_SHOP_CAR_STATUS_NOT_IN_ZERO_POS: 'not in zero pos.',
  T_SHOP_CAR_STATUS_S_ACTIVE: 'SKaktiv',
  T_SHOP_CAR_STATUS_CRANE: 'Crane',
  T_SHOP_CAR_STATUS_S: 'S', // Fahrbefehl Quelle-Daten
  T_SHOP_CAR_STATUS_T: 'T', // Fahrbefehl Ziel-Daten
  T_SHOP_CAR_STATUS_CREQ: 'CReq',
  T_SHOP_CAR_STATUS_REQ: 'Req',
  T_SHOP_CAR_STATUS_WANT_CANCEL: 'WantCancel',
  T_SHOP_CAR_STATUS_CANCEL: 'Cancel',
  T_SHOP_CAR_STATUS_NOT_VALID: 'Not Valid',
  T_SHOP_CAR_STATUS_REF: 'Ref.',
  T_SHOP_CAR_STATUS_RUNNING: 'Running',
  T_SHOP_CAR_STATUS_FINISHED: 'Finished',
  T_SHOP_CAR_STATUS_WILL_END: 'WillEnd',
  T_SHOP_CAR_STATUS_TOS: 'toS',
  T_SHOP_CAR_STATUS_LDS: 'ldS',
  T_SHOP_CAR_STATUS_SLD: 'Sld',
  T_SHOP_CAR_STATUS_TOD: 'toD',
  T_SHOP_CAR_STATUS_DL: 'Dl',
  T_SHOP_CAR_STATUS_DLD: 'Dld',

  T_SHOP_CAR_CONTROL_CRANE: 'Crane SCHED-Mode',
  T_SHOP_CAR_1_SCHED_MODE: 'Car-1 SCHED Mode',
  T_SHOP_CAR_2_SCHED_MODE: 'Car-2 SCHED Mode',
  T_SHOP_CAR_3_SCHED_MODE: 'Car-3 SCHED Mode',
  T_SHOP_CAR_4_SCHED_MODE: 'Car-4 SCHED Mode',
  T_SHOP_CAR_OIL_QUENCH_SCHEDULED: 'Oil Quench in Scheduled',
  T_SHOP_CAR_IN_SCHEDULED_MODE: 'Car in Scheduled Mode',
  T_SHOP_CAR_SCHEDULED: 'Scheduled',
  T_SHOP_STORAGE_SCHEDULED: 'Storage SCHED-Mode',

  // StellPlatz - text under box
  T_SHOP_PLACE_STATUS_FINISHED: 'Finished',
  T_SHOP_PLACE_STATUS_CHECK: 'Check',
  T_SHOP_PLACE_STATUS_ASAP: 'C.Asap',
  T_SHOP_PLACE_STATUS_PCHECK: 'P.Kontr',
  T_SHOP_PLACE_STATUS_WCHECK: 'W.Contr.',
  T_SHOP_PLACE_STATUS_WAIT: 'wait',
  T_SHOP_PLACE_STATUS_MANUAL: 'Manual',

  // SHOPLAYOUT TOOLTIPS
  T_SHOP_TT_LOCK_COMMENT: 'Lock-Comment',
  T_SHOP_TT_LOAD_NO: 'Load N°',
  T_SHOP_TT_IS_ON_PLACE: 'is on Place',
  T_SHOP_TT_PLACE: 'Place',
  T_SHOP_TT_BACK_TO_PLACE: 'BackToWorkplaceN°',
  T_SHOP_TT_PRIORITY: 'Priority',
  T_SHOP_TT_IS_RESERVED_FROM: 'is reserved from',
  T_SHOP_TT_TEST_LOAD: 'Test Load',
  T_SHOP_TT_LONG_LOAD: 'Long Load',
  T_SHOP_TT_PF_NO: 'Process Flow N°',
  T_SHOP_TT_RECIPE: 'Recipe',
  T_SHOP_TT_PLANNED: 'Planned',
  T_SHOP_TT_STARTED: 'Started',
  T_SHOP_TT_PREPARE_FROM: 'Prepare from',
  T_SHOP_TT_STARTING_TIME: 'Starting Time',
  T_SHOP_TT_ENDING_TIME: 'Ending Time',
  T_SHOP_TT_STEP_NO: 'Step N°',
  T_SHOP_TT_PLANNING_SORTING: 'Plannings-Sorting (1-99)',
  T_SHOP_TT_PF_COMPLETED: 'PFlow completed',
  T_SHOP_TT_EMPTY_RACK: 'Empty Rack',
  T_SHOP_TT_NO_INFO: '---',
  T_SHOP_TT_ORDER: 'Order',
  T_SHOP_TT_SERIAL_NO: 'Serial No.',
  T_SHOP_TT_PART_DES: 'PartDes',
  T_SHOP_TT_PART_NO: 'Part n°',
  T_SHOP_TT_CUSTOMER: 'Customer',
  T_SHOP_TT_IN_STAGE: 'in stage:',

  // SHOPLAYOUT, PROCESS FLOWS
  T_SHOP_PF_STATUS_FREE: 'Free',
  T_SHOP_PF_STATUS_OCCUPIED: 'Occupied',
  T_SHOP_PF_STATUS_FINISHED: 'Finished',
  T_SHOP_PF_STATUS_ERROR: 'Error',
  T_SHOP_PF_STATUS_LOCKED: 'LOCKED',
  T_SHOP_PF_STATUS_RESERVED: 'Reserved',
  T_SHOP_PF_STATUS_LOCKED_HIGH_LOAD: 'Locked because of high Load',
  T_SHOP_PF_STATUS_OILY: 'Oily',
  T_SHOP_PF_STATUS_WAIT_FOR_PLC: 'Wait-for-PLC',
  T_SHOP_PF_STATUS_EMPTY_RACK: 'Empty Rack',
  T_SHOP_PF_STATUS_EMPTY_RACK_ANNEALING_GOODS: 'Empty Rack Annealing Goods',
  T_SHOP_PF_STATUS_BASE_TRAY_ANNEALING_GOODS: 'Base tray for annealing goods',
  T_SHOP_PF_STATUS_EMPTY_RACK_STAPLE_GOODS: 'Empty Rack Staple Goods',
  T_SHOP_PF_STATUS_BASE_TRAY_PRODUCTS: 'Base tray for products',
  T_SHOP_PF_STATUS_TEST_LOAD: 'TestLoad',
  T_SHOP_PF_STATUS_BOTTOM_OILY: 'bottom-oily',
  T_SHOP_PF_STATUS_CHECK: 'Check',
  T_SHOP_PF_STATUS_PROCESS_CHECK: 'P.Kontr.',
  T_SHOP_PF_STATUS_WASCH_CHECK: 'W.Kontr.',
  T_SHOP_PF_STATUS_WAIT: 'Wait',
  T_SHOP_PF_STATUS_MANUAL: 'Manual',

  T_SHOP_PF_STATUS_RESERVED_TEXT: 'Reserved by ',
  T_SHOP_PF_STATUS_RESERVED_TEXT_LOAD: 'Cell Load N°:',

  T_SHOP_PF_PRIORITY_NORMAL: 'Normal',
  T_SHOP_PF_PRIORITY_HURRIED: 'Hurried',
  T_SHOP_PF_PRIORITY_VERY_FAST: 'Very fast',
  T_SHOP_PF_PRIORITY_AS_FAST_AS_POSSIBLE: 'As fast as possible',

  // Automat shoplayout legend
  T_SHOP_PF_LEGEND_TEST_PASS: 'Pass',
  T_SHOP_PF_LEGEND_CLEANED: 'Cleaned',
  T_SHOP_PF_LEGEND_PRE_OXIDIZED: 'Pre-oxidized',
  T_SHOP_PF_LEGEND_PRE_HEATED: 'Preheated',
  T_SHOP_PF_LEGEND_TEMPERED: 'Tempered',
  T_SHOP_PF_LEGEND_UNLOAD: 'Unload',
  T_SHOP_PF_LEGEND_EMPTY_RACK: 'Empty Rack',
  T_SHOP_PF_LEGEND_LOCKED: 'Locked',
  T_SHOP_PF_LEGEND_DELAYED_START: 'Starting Time',
  T_SHOP_PF_LEGEND_PREHEATING: '->Preheating',
  T_SHOP_PF_LEGEND_TO_IQ: '->IQ',
  T_SHOP_PF_LEGEND_TO_DROPPING: '->Dropping',
  T_SHOP_PF_LEGEND_TO_COOLING: '->Cooling',
  T_SHOP_PF_LEGEND_TO_WASCHING: '->Washing',
  T_SHOP_PF_LEGEND_TO_TEMPERING: '->Tempering',
  T_SHOP_PF_LEGEND_LOAD_IN_PROGRESS: 'Load in Progress',
  T_SHOP_PF_LEGEND_IN_PROCESS_FLOW: 'In Process Flow',
  T_SHOP_PF_LEGEND_TEST_LOAD: 'TestLoad',
  T_SHOP_PF_LEGEND_FOR_WASHING: 'For Washing',
  T_SHOP_PF_LEGEND_FOR_NITRIDING: 'For Nitriding',
  T_SHOP_PF_LEGEND_TEST_CHARGE_IN_PROGRESS: 'Test Charge in Progress',
  T_SHOP_PF_LEGEND_INCOMPLETE: 'Incomplete',
  T_SHOP_PF_LEGEND_TO_PACKAGING: 'To Packaging',
  T_SHOP_PF_LEGEND_FINISHED_LOAD: 'Finished Load',
  T_SHOP_PF_LEGEND_FINISHED_WITH_ALARMS: 'Load Finished with Alarms',
  T_SHOP_PF_LEGEND_WAIT: 'Wait',
  T_SHOP_PF_LEGEND_TEST_WAIT: 'Wait',
  T_SHOP_PF_LEGEND_TEST_LOAD_URGENT: 'TestLoad Urgent',
  T_SHOP_PF_LEGEND_TEST_PROCESS_INSPECTION: 'Process Inspection',
  T_SHOP_PF_LEGEND_TEST_WASH_INSPECTION: 'Wash Inspection',
  T_SHOP_PF_LEGEND_IN_BETWEEN: 'InBetween',
  T_SHOP_PF_LEGEND_RESERVED: 'Reserved',
  T_SHOP_PF_LEGEND_GR_G: 'GR-G.',
  T_SHOP_PF_LEGEND_LR_G: 'LR-G.',
  T_SHOP_PF_LEGEND_GR_S: 'GR-S.',
  T_SHOP_PF_LEGEND_LR_S: 'LR-S.',

  T_SHOP_PF_MESSAGE_SECOND_LOAD_ASSIGNED: 'To this Load a second Load is assigned, please un-assign first',
  T_SHOP_PF_MESSAGE_CONFIRMATION_NEXT_STAGE: 'Do you really want to manually stop this stage and start the next stage?',
  T_SHOP_PF_MESSAGE_WARNING_DANGEROUS: '!!! ATTENTION !!!  This is a really dangerous action',
  T_SHOP_PF_MESSAGE_NOT_VALID: 'Not Valid',
  T_SHOP_PF_MESSAGE_STEP_MANIPULATION: 'Step manipulation',
  T_SHOP_PF_MESSAGE_ERROR_PLACE: 'Error:  Place:',
  T_SHOP_PF_MESSAGE_ERROR_PF: 'Error: Process flow:',
  T_SHOP_PF_MESSAGE_IS_NOT_OCCUPIED: 'is not occupied',
  T_SHOP_PF_MESSAGE_HAS_NO_LOAD: 'has no load',
  T_SHOP_PF_MESSAGE_HAS_NO_STAGE: 'has no stage',
  T_SHOP_PF_MESSAGE_NO_FURTHER_LOADS_PERMITTED: 'No further loads permitted !',
  T_SHOP_PF_MESSAGE_SELECT_START_TIME: 'Select the start time',
  T_SHOP_PF_MESSAGE_LOAD_READY_NEW_STEP: 'Load is ready. Re-activate because of new step ?',
  T_SHOP_PF_MESSAGE_SELECT_PERSON_FOR_PACK: 'Select a person for <pack Load>',
  T_SHOP_PF_MESSAGE_MAY_NOT_BE_EMPTY: 'may not be empty',
  T_SHOP_PF_MESSAGE_ENTER_BOX_NO: 'Please enter a box N° !',
  T_SHOP_PF_MESSAGE_ENTER_VALID_BOX_NO: 'Please select a valid box n° (10..49)',
  T_SHOP_PF_MESSAGE_CANNOT_PICK_TALL_LOADS: 'can not pick up tall loads',
  T_SHOP_PF_MESSAGE_BOOK_PROCESS_FLOW: 'Book Process Flow',
  T_SHOP_PF_MESSAGE_SELECT_TRAY_CODE: 'Please select a tray code (1..99) !',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_1: 'Please select a load order (1..700)',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_2: 'Please select a load order less than 701 !',
  T_SHOP_PF_MESSAGE_ENTER_VALID_TOOLING: 'Please enter a valid Tooling',
  T_SHOP_PF_MESSAGE_WAIT_PLC_CHECK_PROGRESS: 'Please wait. Check with PLC is in progress ...',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED: 'Target is already occupied. Do you really want to overwrite it?',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED_TRAY: 'PlatzControl.80=Target is already occupied with an empty tray. Do you really want to overwrite it?',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_DELETE: 'is occupied -> perhaps you can delete it.',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_LOAD_DELETE: 'is occupied by a Load -> perhaps you can delete it',
  T_SHOP_PF_MESSAGE_OCCUPY_ALREADY_OCCIPIED: 'Place is already occupied !',
  T_SHOP_PF_MESSAGE_OCCUPY_CONFIRM: 'Do you want to indicate the place as occupied ?',
  T_SHOP_PF_MESSAGE_NO_PF_CONTENT_FOUND: 'No process flow content found',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION: 'Data on Place, but no OCCUPIED-Bit ! Unlock would mean deletion. <Yes> == execute anyway ?',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION_LAST_WARNING: '!!!! Last WARNING !!! unlock means you will DELETE the Place-Data   <Yes>==Delete ?',
  T_SHOP_PF_MESSAGE_NO_RIGHTS_FOR_ACTION: 'You have no Rights for this Action. Please contact your Administrator.',

  // Process flow table columns
  T_SHOP_PF_COLUMN_OPERATION_LABEL: 'Operation',
  T_SHOP_PF_COLUMN_WORKPLACE_LABEL: 'Workplace',
  T_SHOP_PF_COLUMN_RECIPE_LABEL: 'Recipe',
  T_SHOP_PF_COLUMN_DESCRIPTION_LABEL: 'Description',
  T_SHOP_PF_COLUMN_TIME_LABEL: 'Time',
  T_SHOP_PF_COLUMN_TIME_TOOLTIP: 'Wait time at this place (min)',
  T_SHOP_PF_COLUMN_ACK_LABEL: 'R',
  T_SHOP_PF_COLUMN_ACK_TOOLTIP: 'Acknowledge',
  T_SHOP_PF_COLUMN_AUTOSKIP_LABEL: 'A',
  T_SHOP_PF_COLUMN_AUTOSKIP_TOOLTIP: 'AutoSkip',
  T_SHOP_PF_COLUMN_MANUAL_LABEL: 'H',
  T_SHOP_PF_COLUMN_MANUAL_TOOLTIP: 'Manual Start-Stop Step',
  T_SHOP_PF_COLUMN_QM_RELEVAT_LABEL: 'Q',
  T_SHOP_PF_COLUMN_QM_RELEVAT_TOOLTIP: 'QM-Relevant-Step',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_LABEL: 'F',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_TOOLTIP: 'external Treatment',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_LABEL: 'P',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_TOOLTIP: 'Production-Statistic-Relevant-Step',
  T_SHOP_PF_COLUMN_MOVE_STEP_LABEL: 'C',
  T_SHOP_PF_COLUMN_MOVE_STEP_TOOLTIP: 'move Step close to next Step for Automat-Planning',
  T_SHOP_PF_COLUMN_PTIME_LABEL: 'PTime',
  T_SHOP_PF_COLUMN_PTIME_TOOLTIP: 'PlanningTime',
  T_SHOP_PF_COLUMN_MIN_HOLD_LABEL: 'Minimum Time',
  T_SHOP_PF_COLUMN_MIN_HOLD_TOOLTIP: 'Minimum hold time in Recipe (min)',
  T_SHOP_PF_COLUMN_MAX_HOLD_LABEL: 'Max. Time',
  T_SHOP_PF_COLUMN_MAX_HOLD_TOOLTIP: 'Max hold time in Recipe (min)',
  T_SHOP_PF_COLUMN_WISH_TIME_LABEL: 'Plan Time',
  T_SHOP_PF_COLUMN_WISH_TIME_TOOLTIP: 'Planning-Wish-Time[min]',
  T_SHOP_PF_COLUMN_STEPNO_FROM_LABEL: 'Combined From-Step',
  T_SHOP_PF_COLUMN_STEPNO_FROM_TOOLTIP: 'Step-No (from where the Min/Max-Time are valid)',
  T_SHOP_PF_COLUMN_STEPNO_TO_LABEL: 'Combined up to Step',
  T_SHOP_PF_COLUMN_STEPNO_TO_TOOLTIP: 'Step-No (up to where the Min/Max-Time are valid)',
  T_SHOP_PF_COLUMN_TRANSFER_LABEL: 'Transfer SP-Time[Sec]',
  T_SHOP_PF_COLUMN_TRANSFER_TOOLTIP: 'SP-Transfertime in Seconds from Step before to this Step',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_LABEL: 'Transfer Plantime[Min]',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_TOOLTIP: 'Planning-Transfertime in Minutes from Step before to this Step',
  T_SHOP_PF_COLUMN_DROP_TIME_LABEL: 'Drop SP-Time[Sec]',
  T_SHOP_PF_COLUMN_DROP_TIME_TOOLTIP: 'SP-DropTime in Seconds',
  // runtime
  T_SHOP_PF_COLUMN_START_LABEL: 'Start',
  T_SHOP_PF_COLUMN_END_LABEL: 'End',
  T_SHOP_PF_COLUMN_PLANNED_WP_LABEL: 'Planned for Workplace',
  T_SHOP_PF_COLUMN_STATUS_LABEL: 'Status',
  T_SHOP_PF_COLUMN_CHECKED_LABEL: 'Checked',
  T_SHOP_PF_COLUMN_TRANSFER_TIME_LABEL: 'Transfer time (sec)',
  T_SHOP_PF_COLUMN_SOAK_TIME_LABEL: 'Soaktime Meas.Val',
  T_SHOP_PF_COLUMN_SOAK_TIME_TOOLTIP: 'How long was the Soaktime of the Load for this Step',
  T_SHOP_PF_COLUMN_TEMP_AFTER_LABEL: 'Temp.after Planning',
  T_SHOP_PF_COLUMN_TEMP_AFTER_TOOLTIP: 'Which Temperature has the Plant AFTER Planning of this Step',
  T_SHOP_PF_COLUMN_PREPARE_START_LABEL: 'Prepare Starttime',
  T_SHOP_PF_COLUMN_PREPARE_START_TOOLTIP: '(Planned) Start-Time of Preparation for this Step',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_LABEL: 'Dynamic Setpoint',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_TOOLTIP: 'Dynamic Setpoint from Process-Flow-Step (PlanTime..OptPlanning...)',

  // Process flow table columns - parts
  T_SHOP_PF_COLUMN_PART_POS_LABEL: 'Pos',
  T_SHOP_PF_COLUMN_PART_POS_TOOLTIP: 'Position-Number (#)',
  T_SHOP_PF_COLUMN_PART_PARTNO_TOOLTIP: 'Enter PartNo (or klick on list)',
  T_SHOP_PF_COLUMN_PART_DESC_LABEL: 'PartDes:',
  T_SHOP_PF_COLUMN_PART_DESC_TOOLTIP: 'Part Description',
  T_SHOP_PF_COLUMN_PART_AMOUNT_LABEL: 'Amount',
  T_SHOP_PF_COLUMN_PART_AMOUNT_2_LABEL: 'After volume control',
  T_SHOP_PF_COLUMN_PART_AMOUNT_TOTAL_WEIGHT_LABEL: 'Tot. Weight',
  T_SHOP_PF_COLUMN_PART_ORDER_LABEL: 'Order',
  T_SHOP_PF_COLUMN_PART_ORDER_TOOLTIP: 'Enter OrderNo (or klick on list)',
  T_SHOP_PF_COLUMN_PART_ORDER_SELECTION_LABEL: 'Passing',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL: 'Serial No.',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL_2: 'Order Remark',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_3: 'Field-3',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_4: 'Field-4',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_LABEL: 'additional',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_TOOLTIP: 'Edit in a window',
  T_SHOP_PF_COLUMN_PART_TYPE_LABEL: 'Type',
  T_SHOP_PF_COLUMN_PART_TYPE_TOOLTIP: 'Type (Own,Foreign,Earnings)',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_LABEL: '-> Start Part-Window',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_TOOLTIP: 'Launch Basedata Parts',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_LABEL: 'Intern',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_TOOLTIP: 'Order-Id (internal)',
  T_SHOP_PF_COLUMN_PART_PROBE_LABEL: 'Probe',
  T_SHOP_PF_COLUMN_PART_MATERIAL_LABEL: 'Material',
  T_SHOP_PF_COLUMN_PART_DRAWING_NO_LABEL: 'Drawing N°',

  // edit pf
  T_SHOP_PF_DIALOG_EDIT_CAPTION: 'Edit process flow',

  T_SHOP_PF_MESSAGE_PLACE_ALREADY_RESERVED: 'New Booking-Place is already reserved -> so dont changed that',
  T_SHOP_PF_MESSAGE_NUMBER_USED_1: 'Number',
  T_SHOP_PF_MESSAGE_NUMBER_USED_2: 'already used!',
  T_SHOP_PF_MESSAGE_NUMBER_USED_3: 'Number refused!',

  T_SHOP_PF_MESSAGE_NOT_COMPLETE_1: 'in stage',
  T_SHOP_PF_MESSAGE_NOT_COMPLETE_2: 'is not complete',

  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_1: 'Amount is too small -> take ProcessFlow:',
  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_2: 'instead of ProcessFlow:',

  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_1: 'Load Error: No process flow found for part',
  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_2: 'and',

  // delete place
  T_SHOP_PF_DIALOG_DELETE_CAPTION: 'Delete place',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_CAPTION: 'Confirmation before deleting',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_TEXT: 'Do you really want to delete all data of place N° (PFlow+Content) ?',
  T_SHOP_PF_DIALOG_DELETE_PERSON_CAPTION: 'Please select Person who unpacked the load',
  T_SHOP_PF_DIALOG_DELETE_PERSON_ERROR: 'Without a person who has unpacked the load= you cannot delete the place',
  T_SHOP_PF_DIALOG_DELETE_RUNNING_PERMISSION_ERROR: 'No permission to delete a running Load',
  T_SHOP_PF_DIALOG_DELETE_PLACE_NOT_FOUND_ERROR: 'Error:  Place in DB (b_wp_aut) not found:',

  // move place
  T_SHOP_PF_DIALOG_MOVE_TEXT: 'Move the data from this place to place n°',

  // manual command
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_CAPTION: 'Command car manually',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_TEXT: 'Enter man. command for target place:',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_VALID: 'Error Place {0} not valid',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_UNLOAD: 'No manual car command allowed for UNLOADING places.',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_LOADING: 'Can\'t move a load to a LOADING station',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_FOUND_B_WP_AUT: 'Error:  Place: {0} not found in DB b_wp_aut',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_CANNOT_BE_GIVEN: 'A workplace cannot be given a manual command. Please use a process flow.',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_IS_NOT_OCCUPIED: 'Error:  Place: {0} is not occupied',

  // Cancel manual command
  T_SHOP_PF_DIALOG_CANCEL_MANUAL_COMMAND: 'Do you really want to cancel the manual car command ?',

  // Reactivate pf
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_TEXT: 'Reactivate a deleted ProcessFlowNo: (without - : YYNNNNN (YY=Year NNNNN=Number)',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_NOT_FOUND: 'ProcessFlowNo not found',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_ALREADY_ON_PLACE: 'This ProcessFlowNo is already on Place',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_INPUT: 'A ProcessFlowNo must have exactly 7 Characters (2=Year 5=Number)',

  T_SHOP_PF_DIALOG_CAPTION_INFORMATION: 'Information',
  T_SHOP_PF_DIALOG_CAPTION_MOVE: 'Move',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRMATION_OVERWRITE: 'Confirmation before Overwrite',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRM: 'Please confirm',

  T_SHOP_PF_BTN_LOCK_CAPTION: 'Lock',
  T_SHOP_PF_BTN_LOCK_TOOLTIP: 'Lock place',
  T_SHOP_PF_BTN_LOCK_UNLOCK_CAPTION: 'Unlock',
  T_SHOP_PF_BTN_LOCK_UNLOCK_TOOLTIP: 'Unlock place',

  T_SHOP_PF_BTN_UNLOAD_CAPTION: 'Unload',
  T_SHOP_PF_BTN_UNLOAD_TOOLTIP: 'Unload',

  T_SHOP_PF_BTN_LOAD_PARTS_CAPTION: 'Load parts',
  T_SHOP_PF_BTN_LOAD_PARTS_TOOLTIP: 'Load parts',

  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_CAPTION: 'Modify process flow',
  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_TOOLTIP: 'Modify process flow',

  T_SHOP_PF_BTN_START_PROCESS_FLOW_CAPTION: 'Enable start',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_TOOLTIP: 'Enable start',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_CAPTION: 'Modify start time',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_TOOLTIP: 'Modify start time',

  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_CAPTION: 'Enter process flow',
  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_TOOLTIP: 'Enter process flow',

  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_CAPTION: 'Manual Car Command',
  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_TOOLTIP: 'Manual Car Command',

  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION: 'Cancel manual command',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_TOOLTIP: 'Cancel manual command',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION_TO: 'Cancel man. cmd to',

  T_SHOP_PF_BTN_DELETE_PLACE_CAPTION: 'Delete place',
  T_SHOP_PF_BTN_DELETE_PLACE_TOOLTIP: 'Delete place',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_CAPTION: 'Unload',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_TOOLTIP: 'Unload',

  T_SHOP_PF_BTN_OCCUPY_CAPTION: 'Occupy',
  T_SHOP_PF_BTN_OCCUPY_TOOLTIP: 'Occupy',

  T_SHOP_PF_BTN_SHOW_LOADS_CAPTION: 'Show loads',
  T_SHOP_PF_BTN_SHOW_LOADS_TOOLTIP: 'Show loads',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_CAPTION: 'Reactivate ProcessFlowNo',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_TOOLTIP: 'Reactivate a deleted ProcessFlowNo on this emptyTray',

  T_SHOP_PF_BTN_STEP_MANIPULATION_CAPTION: 'Step manipulation',
  T_SHOP_PF_BTN_STEP_MANIPULATION_TOOLTIP: 'Step manipulation',

  T_SHOP_PF_BTN_MOVE_TO_PLACE_CAPTION: 'Move',
  T_SHOP_PF_BTN_MOVE_TO_PLACE_TOOLTIP: 'Move',

  T_SHOP_PF_BTN_SAVE_IMAGE: 'Save Image',
  T_SHOP_PF_BTN_SAVE_IMAGE_EXISTS: 'Image Available',

  T_SHOP_PF_BTN_LOAD_TESTED_CAPTION: 'Finish/Edit load test',
  T_SHOP_PF_BTN_LOAD_TESTED_TOOLTIP: 'Finish/Edit load test',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_CAPTION: 'Start load testing',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_TOOLTIP: 'CheckLoad',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_CAPTION: 'Check OK',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_TOOLTIP: 'In-Between-Check has been done',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_CAPTION: 'P.Kontr. OK',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_TOOLTIP: 'Processcontrol has been done',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_CAPTION: 'W.Contr. OK',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_TOOLTIP: 'WashControl has been done',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_CAPTION: 'End Wait',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_TOOLTIP: 'Wait has been finished',

  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_CAPTION: 'Start/Stop QA processing',
  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_TOOLTIP: 'Start/Stop QA processing',

  T_SHOP_PF_CHECKBOX_TEST_LOAD_CAPTION: 'Test Load',
  T_SHOP_PF_CHECKBOX_TEST_LOAD_TOOLTIP: 'Test Load',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_CAPTION: 'End test urgent',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_TOOLTIP: 'End test urgent',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_CAPTION: 'NIO test',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_TOOLTIP: 'NIO test',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_CAPTION: '..In QA processing..',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_TOOLTIP: '..In QA processing..',

  T_SHOP_PF_UNLOAD_SELECT_PLACE_CAPTION: 'Select Unloading-Place:',
  T_SHOP_PF_UNLOAD_ERROR_MAGAZINE_ONLY: 'Unpack is only possible on Magazin-Places',
  T_SHOP_PF_UNLOAD_ERROR_INVALID_OPTION: 'Invalid place provided for unload!',
  T_SHOP_PF_UNLOAD_ERROR_ALREADY_TARGET: ' (Place already Target of Place: {0})',
  T_SHOP_PF_UNLOAD_ERROR_CAR_APPROACHED: ' (Place right now approached of a car)',

  T_SHOP_PF_TEST_DIALOG_TITLE: 'Load tested',
  T_SHOP_PF_TEST_DIALOG_TEXT: 'Put in Name and Time',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE: 'Put in Name and Time',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE_CAPTION: 'Load tested',
  T_SHOP_PF_TEST_DIALOG_INPUT_TEST_STARTED_TEXT: 'Testload initiated  {0} ',
  T_SHOP_PF_TEST_DIALOG_ERROR_ACTION_NOT_POSSIBLE: 'Action not possible',
  T_SHOP_PF_TEST_DIALOG_ERROR_PLACE_NOT_FOUND: 'Error:  Place in DB (b_wp_aut) not found: {0}',

  T_SHOP_PF_TEST_DIALOG_CONFIRM_NOT_READY_CHECKED: 'Load not yet ready ! Really want to mark as tested ?',
  T_SHOP_PF_TEST_ENTER_RESULT_DIALOG_TEXT: 'Do you want to enter a Test Result ?',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_PASSED: 'Test passed',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_NOT_PASSED: 'Test not passed',

  // LOAD INFO
  T_LOAD_STATUS_WAIT_FOR: 'Wait for',
  T_LOAD_STATUS_PLANNED_START: 'planned start',
  T_LOAD_STATUS_EXTERNAL_START: 'external Start',
  T_LOAD_STATUS_NOT_READY: 'NOT READY!',
  T_LOAD_STATUS_CONT_MANUAL: 'Controller is in Manual-Mode',
  T_LOAD_STATUS_STEP_1: 'of',
  T_LOAD_STATUS_STEP_2: 'Step N°',
  T_LOAD_STATUS_RECIPE_FINISHED: 'Recipe finished',
  T_LOAD_STATUS_OCCUPIED: 'occupied',
  T_LOAD_STATUS_WILL_START: 'will start',
  T_LOAD_STATUS_WAIT_FOR_RECIPE: 'wait for Recipe',
  T_LOAD_STATUS_WAIT_FOR_LOAD: 'wait for Load',
  T_LOAD_STATUS_GET_OUT: 'get out',
  T_LOAD_STATUS_RECIPE: 'Recipe',
  T_LOAD_STATUS_WANT: 'want',
  T_LOAD_STATUS_PF_COMPLETED: 'PFlow completed',
  T_LOAD_STATUS_LOCKED: 'locked',
  T_LOAD_STATUS_PRIORITY: 'Priority',
  T_LOAD_STATUS_NOT_OCCUPIED: '! not occupied !',
  T_LOAD_STATUS_NO_TAG: 'No Tag',
  T_LOAD_STATUS_PREPARE: 'PREPARE',
  T_LOAD_STATUS_RESERVED_BY: 'Reserved by',

  // GENERAL ERRORS
  T_GENERAL_ERROR_NOT_IMPLEMENTED: 'Not implemented for customer!',
  T_GENERAL_ERROR_UNSUPPORTED_ON_WORKPLACE: 'Function is not supported for selected workplace!',
  T_GENERAL_ERROR_CAPTION: 'Error',
  T_GENERAL_WARNING_CAPTION: 'Warning',

  // Global name of part
  T_GENERAL_PART_NAME_1: 'Part n°',
  T_GENERAL_PART_NAME_2: 'Kaefig-Nr.',
  T_GENERAL_PART_NAME_3: 'Bosch-Ch.-Nr.',
  T_GENERAL_PART_NAME_4: 'Ident-Nr',

  // LOAD DIALOG ERRORS
  T_LOAD_DIALOG_ERROR_NO_PARTS: 'No parts provided!',
  T_LOAD_DIALOG_ERROR_ORDER: 'Order(pos) \'{0}\' was found!',
  T_LOAD_DIALOG_ERROR_PLACE_STATE: 'The booking space must be Occupied without data (== empty space).',
  T_LOAD_DIALOG_ERROR_PLACE_ALREADY_OCCUPIED: 'The place is already occupied with data.',
  T_LOAD_DIALOG_ERROR_NOT_OCCUPIED_OR_EMPTY: 'Place is not occupied, or tray is empty!',
  T_LOAD_DIALOG_ERROR_PLACE_LOCKED: 'This place is locked!',
  T_LOAD_DIALOG_ERROR_PF_FROM_ORDER_NOT_FOUND: 'Process flow from order, pfTemplateId={0} was not found.',
  T_LOAD_DIALOG_ERROR_NO_BOX_NO_FREE: 'No Box-No. free --> Please press on Command-Repair...',
  T_LOAD_DIALOG_ERROR_CREATING_LOAD: 'Error while creating a new Cell-Load',
  T_LOAD_DIALOG_ERROR_NO_LOAD_FOUND: 'No load found on workplace {0}',
  T_LOAD_DIALOG_ERROR_NO_PART_INFO_FOUND: 'No information about loaded parts was found for workplace!',
  T_LOAD_DIALOG_ERROR_LOAD_ALREADY_ENABLED: 'Load was already enabled for automatic loading!',
  T_LOAD_DIALOG_ERROR_NO_A_LD_TMT_HEAD: 'Error: a_ld_tmt_head not found for loadId={0}, Place from {1}',
  T_LOAD_DIALOG_ERROR_NO_LOAD: 'Error: Load in place {0} not found a_ld.id={1}',

  T_GANNT_PLANNING_NO_PLANNING_INFO: 'No planning information available',
  T_GANNT_PLANNING_NO_CAR_COMMAND: 'No car command available',

  // BELT FURNACES
  // Loading dialog
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOUND: 'Charge data could not be loaded.',
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOR_BELT: 'Only for beltfurnace charges allowed!',
  T_BELT_LOAD_START_ERROR_ORDER_ALREADY_ACTIVE: 'Not possible. Order already active.',
  T_BELT_LOAD_START_ERROR_ONLY_ONE_ACTIVE_ORDER: 'Only one active Order allowed',
  T_BELT_LOAD_START_ERROR_LOAD_ALREADY_STARTED: 'Not possible. Load already started.',
  T_BELT_LOAD_START_ERROR_LOAD_WAITING_FOR_CONDITION: 'Not possible, because there is already a Load waiting for Condition after this one',
  T_BELT_LOAD_START_ERROR_ACTION_NOT_ALLOWED_FOR_ORDERS: 'Action not allowed for Orders',
  T_BELT_LOAD_START_NEW_STATUS_WAS_FORCED: 'New Status has been forced.',
  T_BELT_LOAD_START_EXECUTION_STOPPED: 'Execution stopped',
  T_BELT_LOAD_START_ACTIVATE_ORDER_CONFIRMATION: 'Do you want to activate the Order ?',
  T_BELT_LOAD_START_LOAD_CONFIRMATION: 'Do you really want to launch the load ?',
  T_BELT_LOAD_START_LOAD_CONDITION_NOT_REACHED_CONFIRMATION: 'Condition haven\'t been reached yet. Are you sure you want to force the load status ?',
  T_BELT_LOAD_START_LOADING_CONFIRMATION: 'Do you really want to start loading ?',
  T_BELT_LOAD_START_LOADING_ERROR_NOT_FOR_THIS_CHARGE: 'Not possible for this charge.',
  T_BELT_LOAD_FINISH_LOADING_DELETE_ORDER_CONFIRMATION: 'Do you really want to delete Order (with all not-yet-started loads) ?',
  T_BELT_LOAD_FINISH_LOADING_STOP_CONFIRMATION: 'Do you really want to stop loading ?',

  // BATCH FURNACES
  T_BATCH_ERROR_CHAMBER_NOT_REMOTE_OR_ACTIVE: 'Error starting planned Load. Chamber not in remote or active mode ...',

  // OTHER
  T_END: 'End',
} satisfies Record<Tpt9800TrlTokens, string>;

const batchRecipeItemVersion: Partial<TComponentPT9800Keys<'batchRecipeItemVersion'>> = {
  correctionProfile: 'Correction profile',
  positions: 'Steps',
};

const digitalInput: Partial<TComponentPT9800Keys<'digitalInput'>> = {
  useForEndControl: 'Use as end condition',
};

export default {
  batchRecipeItemVersion,
  beltRecipeItemHeader,
  digitalInput,
  furnaceAlarm,
  load,
  loadState,
  material,
  planLoadInput,
  plannedPartInput,
  processFlow,
  processFlowContent,
  processFlowListItem,
  processFlowManualCommand,
  pt9800Translations,
  pusherRecipeItemHeader,
  recipeRuntimeInfo,
  recipeItemHeader,
  recipeItemPosition,
  recipeItemPositionHeader,
  recipeListItem,
  setPointItem,
  WorkPlaceStatus,
  WorkPlaceStatusStatus,
};
